import { motion } from "framer-motion";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart,
} from "recharts";
import MovingBackground from "./MovingBackground";

const ease = [0.16, 1, 0.3, 1] as const;

const barData = [
  { name: "Q1", adoption: 25, governance: 40 },
  { name: "Q2", adoption: 45, governance: 55 },
  { name: "Q3", adoption: 68, governance: 72 },
  { name: "Q4", adoption: 88, governance: 85 },
];

const lineData = [
  { month: "Jan", roi: 10 }, { month: "Feb", roi: 18 },
  { month: "Mar", roi: 32 }, { month: "Apr", roi: 45 },
  { month: "May", roi: 62 }, { month: "Jun", roi: 78 },
];

const pieData = [
  { name: "Copilot", value: 35 },
  { name: "Agents", value: 25 },
  { name: "Security", value: 20 },
  { name: "Governance", value: 20 },
];

const COLORS = [
  "hsl(24, 94%, 53%)", "hsl(24, 94%, 63%)",
  "hsl(160, 60%, 45%)", "hsl(220, 15%, 40%)",
];

const GraphSlide = ({ thumbnail }: { thumbnail?: boolean }) => (
  <div className="relative w-full h-full flex flex-col items-center justify-center bg-background overflow-hidden">
    <MovingBackground variant="grid" />

    <div className="relative z-10 w-full max-w-6xl px-6 md:px-12">
      <motion.div
        className="text-center mb-6 md:mb-10"
        initial={thumbnail ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease }}
      >
        <p className="label-text mb-2" style={{ fontSize: thumbnail ? "0.5rem" : undefined }}>
          Analytics & Insights
        </p>
        <h2
          className="font-display font-bold text-foreground"
          style={{ fontSize: thumbnail ? "0.9rem" : "clamp(1.5rem, 3vw, 2.5rem)" }}
        >
          Data‑Driven <span className="text-gradient">AI Adoption</span>
        </h2>
      </motion.div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
        {/* Bar Chart */}
        <motion.div
          className="bg-card/80 backdrop-blur-sm rounded-xl p-4 border border-border/50"
          initial={thumbnail ? false : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6, ease }}
        >
          <p className="text-xs font-display font-semibold text-foreground mb-3">
            Adoption vs Governance
          </p>
          <div style={{ height: thumbnail ? 60 : 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 15%, 16%)" />
                <XAxis dataKey="name" tick={{ fill: "hsl(215, 15%, 55%)", fontSize: thumbnail ? 6 : 11 }} />
                <YAxis tick={{ fill: "hsl(215, 15%, 55%)", fontSize: thumbnail ? 6 : 11 }} />
                {!thumbnail && <Tooltip contentStyle={{ background: "hsl(220, 18%, 10%)", border: "1px solid hsl(220, 15%, 16%)", borderRadius: 8, fontSize: 12 }} />}
                <Bar dataKey="adoption" fill="hsl(24, 94%, 53%)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="governance" fill="hsl(160, 60%, 45%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Area Chart */}
        <motion.div
          className="bg-card/80 backdrop-blur-sm rounded-xl p-4 border border-border/50"
          initial={thumbnail ? false : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.6, ease }}
        >
          <p className="text-xs font-display font-semibold text-foreground mb-3">
            ROI Growth (%)
          </p>
          <div style={{ height: thumbnail ? 60 : 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={lineData}>
                <defs>
                  <linearGradient id="roiGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(24, 94%, 53%)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(24, 94%, 53%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 15%, 16%)" />
                <XAxis dataKey="month" tick={{ fill: "hsl(215, 15%, 55%)", fontSize: thumbnail ? 6 : 11 }} />
                <YAxis tick={{ fill: "hsl(215, 15%, 55%)", fontSize: thumbnail ? 6 : 11 }} />
                {!thumbnail && <Tooltip contentStyle={{ background: "hsl(220, 18%, 10%)", border: "1px solid hsl(220, 15%, 16%)", borderRadius: 8, fontSize: 12 }} />}
                <Area type="monotone" dataKey="roi" stroke="hsl(24, 94%, 53%)" fillOpacity={1} fill="url(#roiGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Pie Chart */}
        <motion.div
          className="bg-card/80 backdrop-blur-sm rounded-xl p-4 border border-border/50 col-span-2 md:col-span-1"
          initial={thumbnail ? false : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6, ease }}
        >
          <p className="text-xs font-display font-semibold text-foreground mb-3">
            Focus Distribution
          </p>
          <div style={{ height: thumbnail ? 60 : 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={thumbnail ? 15 : 45}
                  outerRadius={thumbnail ? 28 : 75}
                  dataKey="value"
                  strokeWidth={0}
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                {!thumbnail && <Tooltip contentStyle={{ background: "hsl(220, 18%, 10%)", border: "1px solid hsl(220, 15%, 16%)", borderRadius: 8, fontSize: 12 }} />}
              </PieChart>
            </ResponsiveContainer>
          </div>
          {!thumbnail && (
            <div className="flex flex-wrap gap-3 mt-2 justify-center">
              {pieData.map((d, i) => (
                <div key={d.name} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i] }} />
                  {d.name}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  </div>
);

export default GraphSlide;
